import { applyStops, parseDamageStops, stopLineText } from './damageStops.js';
import { damageText, parseDamageSpec } from './attack.js';
import { boostedTotal, parseDiceBoost } from '../dice/dice.js';
import { diceRange, diceText } from './system.js';
import { ADVANTAGE_LIMITS, advantageDice, advantageText, isAdvantageWord, parseAdvantageFacts } from './advantage.js';
import { keepDice, poolResult, poolText } from '../dice/pool.js';
import { parseSaveTags } from './modifiers.js';
export const ON_SAVE = ['half', 'none', 'full'];
export const SAVE_LIMITS = { dc: 1000, name: 80, targets: 100, bonus: 1000, diceCount: 20 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
/** One line of plain text: no control character at all (the review of 2026-10-10: a NUL was accepted). */
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/**
 * A spell's save line ("Reflex half", "Fortitude negates", "Will partial") read against the system's saves, by label,
 * case-insensitively: the save, and what a success does — half, none ("negates"), or anything else left to the DM
 * (`full`, with `leftToDm`). Null when it names none of the system's saves ("None", "See text").
 */
export function parseSaveLine(text, saves) {
    const t = String(text ?? '').trim().toLowerCase();
    const found = [...saves].sort((a, b) => b.label.length - a.label.length).find((s) => t.startsWith(s.label.toLowerCase()));
    if (!found)
        return null;
    const rest = t.slice(found.label.length).trim();
    if (/^half\b/.test(rest))
        return { save: found.key, onSave: 'half' };
    if (/^negates\b/.test(rest))
        return { save: found.key, onSave: 'none' };
    return { save: found.key, onSave: 'full', leftToDm: true };
}
/** A resolve request from the wire: exact shape or null. Untrusted input, always. */
export function parseSaveRequest(v) {
    if (!isObj(v) || v.kind !== 'area.resolve' || !UUID_RE.test(String(v.areaId)))
        return null;
    if (typeof v.save !== 'string' || !KEY_RE.test(v.save) || !isInt(v.dc, 1, SAVE_LIMITS.dc))
        return null;
    if (typeof v.onSave !== 'string' || !ON_SAVE.includes(v.onSave))
        return null;
    const damage = v.damage === undefined ? null : parseDamageSpec(v.damage);
    if (v.damage !== undefined && !damage)
        return null;
    const boost = v.boost === undefined ? null : parseDiceBoost(v.boost);
    if (v.boost !== undefined && (!boost || !damage))
        return null;
    const priv = v.private === undefined ? false : v.private;
    if (typeof priv !== 'boolean')
        return null;
    if (v.name !== undefined && !isText(v.name, SAVE_LIMITS.name))
        return null;
    if (v.caster !== undefined && !UUID_RE.test(String(v.caster)))
        return null;
    if (v.improved !== undefined && !(Array.isArray(v.improved) && v.improved.length <= 64 && v.improved.every((x) => UUID_RE.test(String(x)))))
        return null;
    const against = v.against === undefined ? undefined : parseSaveTags(v.against);
    if (against === null)
        return null;
    if (v.advantage !== undefined && !(isObj(v.advantage) && Object.keys(v.advantage).length <= SAVE_LIMITS.targets && Object.entries(v.advantage).every(([k, x]) => UUID_RE.test(k) && isAdvantageWord(x))))
        return null;
    return {
        kind: 'area.resolve', areaId: String(v.areaId), save: v.save, dc: v.dc, onSave: v.onSave, private: priv,
        ...(damage ? { damage } : {}), ...(boost ? { boost } : {}), ...(typeof v.name === 'string' ? { name: v.name.trim() } : {}),
        ...(typeof v.caster === 'string' ? { caster: v.caster } : {}), ...(Array.isArray(v.improved) && v.improved.length ? { improved: v.improved.map(String) } : {}),
        ...(isObj(v.advantage) && Object.keys(v.advantage).length ? { advantage: { ...v.advantage } } : {}),
        ...(against ? { against } : {}),
    };
}
/** The ruling a resolve against the save `key` is made by, or null when the system has no saves or no such save. */
export function saveRuling(combat, key, borrowed) {
    const s = combat?.saves;
    const kind = s?.list.find((x) => x.key === key);
    if (!s || !kind)
        return null;
    return { dice: advantageDice(s.dice, null), natural: s.natural.map((n) => ({ ...n })), save: { key: kind.key, label: kind.label }, minimumDamage: combat.minimumDamage, ...(borrowed ? { borrowed } : {}) };
}
/** What a system without saving throws is told (issue 1305): no automatic saves. */
export const NO_SAVE_RULES = "This table's game system has no saving throw rules, so the table cannot roll a spell's saves.";
function parseSaveRuling(v) {
    if (!isObj(v) || !isObj(v.dice) || !isInt(v.dice.count, 1, SAVE_LIMITS.diceCount) || !isInt(v.dice.sides, 2, 1000))
        return null;
    const dice = { count: v.dice.count, sides: v.dice.sides };
    const { min, max } = diceRange(dice);
    if (!Array.isArray(v.natural) || v.natural.length > 24)
        return null;
    const natural = [];
    for (const n of v.natural) {
        if (!isObj(n) || !isInt(n.face, min, max) || (n.result !== 'success' && n.result !== 'failure'))
            return null;
        natural.push({ face: n.face, result: n.result });
    }
    if (!isObj(v.save) || typeof v.save.key !== 'string' || !KEY_RE.test(v.save.key) || !isText(v.save.label, 60))
        return null;
    if (v.minimumDamage !== null && !isInt(v.minimumDamage, 0, 1000))
        return null;
    if (v.borrowed !== undefined && !isText(v.borrowed, 120))
        return null;
    return { dice, natural, save: { key: v.save.key, label: v.save.label }, minimumDamage: v.minimumDamage, ...(typeof v.borrowed === 'string' ? { borrowed: v.borrowed } : {}) };
}
function parseTarget(v, dice) {
    if (!isObj(v) || !UUID_RE.test(String(v.id)) || typeof v.name !== 'string' || v.name.length > 200 || typeof v.seen !== 'boolean' || typeof v.named !== 'boolean')
        return null;
    if (!Array.isArray(v.owners) || !v.owners.every((o) => UUID_RE.test(String(o))) || typeof v.ownersSee !== 'boolean')
        return null;
    if (v.bonus !== null && !isInt(v.bonus, -SAVE_LIMITS.bonus, SAVE_LIMITS.bonus))
        return null;
    const advantage = v.advantage === undefined ? null : parseAdvantageFacts(v.advantage);
    if (v.advantage !== undefined && !advantage)
        return null;
    const extra = !Array.isArray(v.faces) ? 0 : v.faces.length - dice.count;
    if (v.autoFail !== undefined && !(typeof v.autoFail === 'string' && v.autoFail.length >= 1 && v.autoFail.length <= ADVANTAGE_LIMITS.name && Array.isArray(v.faces) && !v.faces.length))
        return null;
    if (!Array.isArray(v.faces) || (v.autoFail !== undefined ? false : advantage?.result ? extra < 1 || extra > ADVANTAGE_LIMITS.extraDice : extra !== 0) || !v.faces.every((f) => isInt(f, 1, dice.sides)))
        return null;
    let settled;
    if (v.settled !== undefined) {
        const s = v.settled;
        if (!isObj(s) || !(s.amount === null || isInt(s.amount, 0, 100_000)) || typeof s.auto !== 'boolean')
            return null;
        settled = { amount: s.amount, auto: s.auto };
    }
    if (v.seenBy !== undefined && (!Array.isArray(v.seenBy) || v.seenBy.length > 64 || !v.seenBy.every((o) => UUID_RE.test(String(o)))))
        return null;
    const terms = v.terms === undefined ? undefined : parseTerms(v.terms, 10);
    if (terms === null)
        return null;
    if (v.asks !== undefined && v.asks !== true)
        return null;
    if (v.notes !== undefined && !(Array.isArray(v.notes) && v.notes.length >= 1 && v.notes.length <= 4 && v.notes.every((n) => isText(n, 80))))
        return null;
    const stops = v.stops === undefined ? undefined : parseDamageStops(v.stops);
    if (stops === null)
        return null;
    if (v.rethrown !== undefined && !(isObj(v.rethrown) && typeof v.rethrown.by === 'string' && v.rethrown.by.length <= 120 && Array.isArray(v.rethrown.was) && v.rethrown.was.length === v.faces.length && v.rethrown.was.every((f) => isInt(f, 1, dice.sides))))
        return null;
    return { ...(typeof v.autoFail === 'string' ? { autoFail: v.autoFail } : {}), ...(advantage ? { advantage } : {}), ...(v.rethrown !== undefined ? { rethrown: v.rethrown } : {}), ...(typeof v.overridden === 'string' && v.overridden.length <= 120 ? { overridden: v.overridden } : {}), ...(stops ? { stops } : {}), ...(v.asks ? { asks: true } : {}), id: String(v.id), name: v.name, seen: v.seen, named: v.named, owners: v.owners.map(String), ownersSee: v.ownersSee, ...(Array.isArray(v.seenBy) && v.seenBy.length ? { seenBy: v.seenBy.map(String) } : {}), bonus: v.bonus, faces: [...v.faces], ...(settled ? { settled } : {}), ...(terms && terms.length ? { terms } : {}), ...(Array.isArray(v.notes) ? { notes: v.notes.map(String) } : {}) };
}
function parseTerms(v, max) {
    if (!Array.isArray(v) || v.length > max)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !isText(x.label, 80) || !isInt(x.value, -1000, 1000))
            return null;
        out.push({ label: x.label, value: x.value });
    }
    return out;
}
/** Stored facts: exact shape or null. The damage dice must match the damage spec, and every creature's the save's dice. */
export function parseSaveFacts(v) {
    if (!isObj(v) || !UUID_RE.test(String(v.area)) || !isText(v.name, SAVE_LIMITS.name) || typeof v.save !== 'string' || !KEY_RE.test(v.save))
        return null;
    if (!isInt(v.dc, 1, SAVE_LIMITS.dc) || typeof v.onSave !== 'string' || !ON_SAVE.includes(v.onSave))
        return null;
    const rules = parseSaveRuling(v.rules);
    if (!rules || rules.save.key !== v.save)
        return null;
    const damage = v.damage === undefined ? null : parseDamageSpec(v.damage);
    if (v.damage !== undefined && !damage)
        return null;
    let damageRoll;
    if (damage || v.damageRoll !== undefined) {
        const r = v.damageRoll;
        if (!damage || !Array.isArray(r) || r.length !== damage.parts.length)
            return null;
        if (!damage.parts.every((p, i) => Array.isArray(r[i]) && r[i].length === p.count && r[i].every((f) => isInt(f, 1, p.sides))))
            return null;
        damageRoll = r.map((x) => [...x]);
    }
    if (v.scene !== undefined && !UUID_RE.test(String(v.scene)))
        return null;
    if (!Array.isArray(v.targets) || v.targets.length < 1 || v.targets.length > SAVE_LIMITS.targets)
        return null;
    const targets = [];
    for (const t of v.targets) {
        const got = parseTarget(t, rules.dice);
        if (!got)
            return null;
        targets.push(got);
    }
    if (new Set(targets.map((t) => t.id)).size !== targets.length)
        return null;
    const terms = v.terms === undefined ? undefined : parseTerms(v.terms, 30);
    if (terms === null)
        return null;
    const boost = v.boost === undefined ? null : parseDiceBoost(v.boost);
    if (v.boost !== undefined && !boost)
        return null;
    if (v.heals !== undefined && v.heals !== true)
        return null;
    return {
        ...(v.heals ? { heals: true } : {}),
        area: String(v.area), name: v.name.trim(), save: v.save, dc: v.dc, onSave: v.onSave, ...(damage ? { damage } : {}), ...(damageRoll ? { damageRoll } : {}), ...(boost ? { boost } : {}),
        ...(v.scene !== undefined ? { scene: String(v.scene) } : {}), targets, rules, ...(terms && terms.length ? { terms } : {}),
    };
}
/** The roll's own dice: every creature's save faces, in the order they were caught. */
export const saveFaces = (f) => f.targets.flatMap((t) => t.faces);
/**
 * One creature's save (design/29 §7): a natural result the system names decides whatever the total (3.5: 20 succeeds,
 * 1 fails); otherwise its total against the DC; with no bonus known, it waits for the DM.
 */
/**
 * A linked character's bonus on a save, from the numbers its sheet holds (issue 1505; docs/MRCS/feats.md §7): the
 * sheet's saving throw whose words name the save (its key, `reflex`, or a word of its label), as a rule block's sheet
 * Direct finds it. When the sheet itemised it, the base is the bonus and each line after it a term of the save's own —
 * *Lightning Reflexes +2* — so the DM's line shows the feat; the total is the sheet's number either way. Null when the
 * sheet holds no such number.
 */
export function sheetSaveOf(sheet, save) {
    const words = new Set([save.key.toLowerCase(), ...save.label.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)]);
    const n = (sheet ?? []).find((x) => x.purpose === 'saving_throw' && x.words.some((w) => words.has(w)));
    if (!n || !Number.isInteger(n.value) || Math.abs(n.value) > SAVE_LIMITS.bonus)
        return null;
    if (!n.lines || n.lines.length < 2)
        return { bonus: n.value };
    return { bonus: n.lines[0].value, terms: n.lines.slice(1).map((l) => ({ label: l.label, value: l.value })) };
}
/** A creature's throw as a pool, when advantage made it one: which die counted. */
export function savePool(t) {
    const result = t.advantage?.result;
    if (!result)
        return null;
    const dice = { count: t.faces.length, sides: 1, keep: { which: result === 'advantage' ? 'highest' : 'lowest', n: 1 } };
    return { dice, pool: keepDice(dice, t.faces.map((f) => ({ faces: [f] }))) };
}
export function saveOutcome(t, f) {
    if (t.overridden) {
        const { overridden: _by, ...thrown } = t;
        return { ...saveOutcome(thrown, f), result: 'saved' };
    }
    if (t.autoFail)
        return { natural: 0, total: null, result: 'failed' };
    const kept = savePool(t);
    const natural = kept ? poolResult(kept.dice, kept.pool) : t.faces.reduce((a, b) => a + b, 0);
    const total = t.bonus === null ? null : natural + t.bonus + [...(f.terms ?? []), ...(t.terms ?? [])].reduce((n, x) => n + x.value, 0);
    const named = f.rules.natural.find((n) => n.face === natural);
    if (named)
        return { natural, total, result: named.result === 'success' ? 'saved' : 'failed' };
    if (total === null)
        return { natural, total, result: 'unresolved' };
    return { natural, total, result: total >= f.dc ? 'saved' : 'failed' };
}
/** The spell's damage roll as a total: its dice and bonus, at least the system's minimum (or nothing). Null with no damage. */
export function spellDamage(f) {
    if (!f.damage || !f.damageRoll)
        return null;
    const dice = f.damageRoll.reduce((s, part) => s + part.reduce((a, b) => a + b, 0), 0);
    if (f.boost)
        return Math.max(f.rules.minimumDamage ?? 0, boostedTotal(dice, f.damage.parts.reduce((s, p) => s + p.count * p.sides, 0), f.damage.bonus, f.boost));
    return Math.max(f.rules.minimumDamage ?? 0, dice + f.damage.bonus);
}
/** Half damage: rounded down, and nothing below 0 (the data names no rounding rule; 3.5 rounds fractions down). */
export const halfOf = (n) => Math.max(0, Math.floor(n / 2));
/** The damage one creature takes: full on a failed save, as the save line says on a success; null with no damage or no outcome yet. */
export function targetDamage(f, t) {
    return targetDealt(f, t)?.total ?? null;
}
/** One creature's share, after its save and then what stops it, with the DM's lines for the second. */
export function targetDealt(f, t) {
    const full = spellDamage(f);
    if (full === null)
        return null;
    if (f.heals)
        return { total: full, lines: [] };
    const o = saveOutcome(t, f);
    if (o.result === 'unresolved')
        return null;
    const share = o.result === 'failed' ? full : f.onSave === 'half' ? halfOf(full) : f.onSave === 'none' ? 0 : full;
    if (!t.stops)
        return { total: share, lines: [] };
    // The spell as one piece, of the types its dice name: an area of mixed dice is rare, and the DM's Change is beside it.
    const types = [...new Set((f.damage?.parts ?? []).flatMap((p) => p.types ?? []))];
    return applyStops(t.stops.rules, t.stops.creature, [{ types, amount: share }], t.stops.overcome);
}
/** "14, [3]: Advantage (chosen)": the dice, when there are two, and what decided it. */
const saveAdvantageWords = (t) => {
    const kept = savePool(t);
    return `${kept ? `${poolText(kept.pool)}: ` : ''}${advantageText(t.advantage)}`;
};
const sees = (t, viewerId) => t.seen || (t.seenBy?.includes(viewerId) ?? false) || (t.ownersSee && t.owners.includes(viewerId));
/**
 * A player's version of a resolve, or null when it caught no creature they can see. A hidden creature is in no form;
 * a monster's bonus never reaches them; their own creature's dice and total do, on a public resolve.
 */
export function playerSave(roll, viewerId) {
    const f = roll.save;
    if (!f)
        return null;
    const seen = f.targets.filter((t) => sees(t, viewerId));
    if (!seen.length)
        return null;
    const caster = !roll.private && roll.by === null && roll.actor.id === viewerId;
    return {
        id: roll.id, at: roll.at, actor: { ...roll.actor },
        name: roll.private ? null : f.name,
        save: f.rules.save.label,
        dc: caster ? f.dc : null,
        damage: caster ? spellDamage(f) : null,
        ...(f.heals ? { heals: true } : {}),
        dice: { ...f.rules.dice },
        targets: seen.map((t) => {
            const o = saveOutcome(t, f);
            const own = !roll.private && !f.heals && t.ownersSee && t.owners.includes(viewerId);
            return { name: t.named || t.owners.includes(viewerId) ? t.name : null, result: o.result, own: own ? { natural: o.natural, total: o.total, ...(t.autoFail ? { noDie: t.autoFail } : {}), ...(t.rethrown ? { again: true } : {}), ...(t.advantage ? { advantage: saveAdvantageWords(t) } : {}) } : null };
        }),
        area: f.area,
        ...(caster && f.rules.borrowed ? { borrowed: f.rules.borrowed } : {}),
    };
}
export function parsePlayerSave(v) {
    if (!isObj(v) || typeof v.id !== 'string' || typeof v.at !== 'string' || typeof v.save !== 'string' || !isObj(v.actor) || !isObj(v.dice) || !Array.isArray(v.targets))
        return null;
    return v;
}
// ── The lines ─────────────────────────────────────────────────────────────
const ON_SAVE_WORDS = { half: 'half on a save', none: 'none on a save', full: 'full damage either way' };
const resultWords = (r) => (r === 'unresolved' ? 'waiting for the DM' : r);
const signed = (n) => (n < 0 ? `− ${-n}` : `+ ${n}`);
/** `nameOf` names a creature as the board does now (qa-runs 149: "explore-token 4", not three alike); the record's name without it. */
export function dmSaveRows(roll, nameOf = (t) => t.name) {
    const f = roll.save;
    if (!f)
        return [];
    return f.targets.map((t) => {
        const name = nameOf(t);
        const o = saveOutcome(t, f);
        const dealt = targetDealt(f, t);
        const d = dealt?.total ?? null;
        const stopped = dealt?.lines.length ? ` (${dealt.lines.map(stopLineText).join('; ')})` : '';
        const kept = savePool(t);
        const throwText = `${kept ? diceText({ ...kept.dice, sides: f.rules.dice.sides }) : diceText(f.rules.dice)} (${kept ? poolText(kept.pool) : t.faces.join(', ')}${t.rethrown ? `, thrown again by ${t.rethrown.by}; first ${t.rethrown.was.join(', ')}` : ''})${t.advantage ? ` [${advantageText(t.advantage)}]` : ''}`;
        const extra = [...(f.terms ?? []), ...(t.terms ?? [])].map((x) => ` ${signed(x.value)} ${x.label}`).join('');
        const working = t.autoFail ? `no die (${t.autoFail})` : t.bonus === null ? `${throwText}, no ${f.rules.save.label} bonus known` : `${throwText} ${signed(t.bonus)}${extra} = ${o.total}`;
        const settled = t.settled ? (t.settled.amount === null ? ', dropped' : `, ${t.settled.auto ? 'applied at once' : 'applied'}`) : '';
        const unknown = `${t.notes?.length ? ` Not counted: ${t.notes.join('; ')}.` : ''}${t.asks ? ' Its kind is not known: Apply if it is one, Drop if not.' : ''}`;
        if (f.heals)
            return { id: t.id, name, line: `${name}: heals ${t.settled?.amount ?? d}${settled}.${unknown}`, result: o.result, damage: d, settled: t.settled ?? null, bonusKnown: true, ...(t.asks ? { asks: true } : {}) };
        return { id: t.id, name, line: `${name}: ${working} vs DC ${f.dc}: ${resultWords(o.result)}${t.overridden ? ` by ${t.overridden}` : ''}${d !== null ? ` — ${t.settled?.amount ?? d} damage${stopped}${settled}` : ''}${unknown}`, result: o.result, damage: d, settled: t.settled ?? null, bonusKnown: t.bonus !== null || !!t.autoFail };
    });
}
/** The DM's whole line for a resolve: the spell, its save and DC, the damage roll, then one line a creature. */
export function dmSaveLines(roll) {
    const f = roll.save;
    if (!f)
        return [];
    if (f.heals)
        return [`${f.name}: heals ${spellDamage(f)} (${f.damageRoll?.flat().join(', ')})`, ...dmSaveRows(roll).map((r) => r.line)];
    const lines = [`${f.name}: ${f.rules.save.label} DC ${f.dc}${f.damage ? `, ${ON_SAVE_WORDS[f.onSave]}` : ''}${f.rules.borrowed ? ` (${f.rules.borrowed} rules)` : ''}`];
    // A changed cast's dice do not add up to the total on their own: say what changed them (qa-runs#366).
    const boosted = f.boost ? `${f.boost.maximum ? ', each die at its most' : ''}${f.boost.share ? `, plus ${f.boost.share[0]}/${f.boost.share[1]}` : ''}` : '';
    if (f.damage && f.damageRoll)
        lines.push(`damage: ${damageText(f.damage)} (${f.damageRoll.flat().join(', ')})${boosted} = ${spellDamage(f)}`);
    return [...lines, ...dmSaveRows(roll).map((r) => r.line)];
}
/** A player's lines: "Fireball — Reflex saves", then each creature they can see, with their own numbers. */
export function playerSaveLines(p) {
    if (p.heals)
        return [`${p.name ?? 'A spell'} — heals${p.damage !== null ? ` ${p.damage}` : ''}`, ...p.targets.map((t) => t.name ?? 'A creature')];
    const head = `${p.name ? `${p.name} — ` : ''}${p.save} saves${p.dc !== null ? ` against DC ${p.dc}` : ''}${p.damage !== null ? `, ${p.damage} damage` : ''}`;
    return [head, ...p.targets.map((t) => {
            const who = t.name ?? 'A creature';
            const nums = t.own?.noDie ? ` (no die: ${t.own.noDie})` : t.own ? ` (${diceText(p.dice)} ${t.own.natural}${t.own.total !== null ? `, total ${t.own.total}` : ''}${t.own.again ? ', thrown again' : ''}${t.own.advantage ? `; ${t.own.advantage}` : ''})` : '';
            return `${who}: ${resultWords(t.result)}${nums}`;
        })];
}
/** One creature's save thrown again (`save.rethrow`): its new faces, what it first came to, and the roll's dice to match. */
export function rethrowSave(roll, target, by, faces) {
    if (!roll.save)
        return roll;
    const targets = roll.save.targets.map((t) => (t.id !== target ? t : { ...t, faces: [...faces], rethrown: { was: [...t.faces], by: by.slice(0, 120) } }));
    const save = { ...roll.save, targets };
    return { ...roll, dice: saveFaces(save), save };
}
/** One creature's failed save made a success by a pool (`save_override`): its dice stand, and its label is stamped. */
export function overrideSave(roll, target, by) {
    if (!roll.save)
        return roll;
    return { ...roll, save: { ...roll.save, targets: roll.save.targets.map((t) => (t.id !== target ? t : { ...t, overridden: by.slice(0, 120) })) } };
}
/** Whether a stored roll's dice are its resolve's faces (a record whose dice and facts disagree is refused). */
export const saveDiceMatch = (dice, f) => {
    const faces = saveFaces(f);
    return dice.length === faces.length && dice.every((d, i) => d === faces[i]);
};
/** A resolve with one creature's bonus supplied or its damage settled folded in (`save.bonus`, `save.apply`), as the log reads it back. */
export function foldSave(roll, body) {
    // A throw again is stamped whole, its faces the server's: the roll's dice with them.
    if (body.kind === 'save.rethrow')
        return body.record?.save ? { ...roll, dice: [...body.record.dice], save: body.record.save } : roll;
    if (!roll.save)
        return roll;
    const targets = roll.save.targets.map((t) => (t.id !== body.target ? t
        : body.kind === 'save.bonus' ? { ...t, bonus: body.bonus } : { ...t, settled: { amount: body.amount, auto: body.auto === true } }));
    return { ...roll, save: { ...roll.save, targets } };
}
//# sourceMappingURL=save.js.map